<template>
    <div class="users-container">
        <!-- Header Section -->
        <div class="page-header">
            <div class="header-content">
                <div class="header-left">
                    <div class="header-icon">
                        <b-icon icon="people-fill" font-scale="2"></b-icon>
                    </div>
                    <div class="header-text">
                        <h1 class="page-title">Gestion de Usuarios</h1>
                        <p class="page-subtitle">Administra los usuarios del sistema</p>
                    </div>
                </div>
                <div class="header-right">
                    <button class="btn-create" @click="abrirModalUsuario(null)">
                        <b-icon icon="person-plus-fill" class="mr-2"></b-icon>
                        Nuevo Usuario
                    </button>
                </div>
            </div>
        </div>

        <!-- Main Content -->
        <div class="content-card">

            <!-- Loading State -->
            <div v-if="loading" class="loading-container">
                <div class="loading-content">
                    <b-spinner variant="primary" class="loading-spinner"></b-spinner>
                    <p class="loading-text">Cargando usuarios...</p>
                </div>
            </div>

            <!-- Users Table -->
            <div v-else class="table-container">
                <div class="custom-table">
                    <div class="table-header">
                        <div class="table-row header-row">
                            <div class="table-cell">#</div>
                            <div class="table-cell">Nombre</div>
                            <div class="table-cell">Correo</div>
                            <div class="table-cell">Rol</div>
                            <div class="table-cell">Estado</div>
                            <div class="table-cell">Acciones</div>
                        </div>
                    </div>
                    <div class="table-body">
                        <div v-for="(usuario, index) in usuarios" 
                             :key="usuario.id" 
                             class="table-row data-row">
                            <div class="table-cell">
                                {{ index + 1 + (paginaActual - 1) * 10 }}
                            </div>
                            <div class="table-cell">
                                <div class="user-info">
                                    <div class="user-avatar">
                                        <b-icon icon="person-circle" font-scale="1.5"></b-icon>
                                    </div>
                                    <span class="user-name">{{ usuario.name }}</span>
                                </div>
                            </div>
                            <div class="table-cell">{{ usuario.email }}</div>
                            <div class="table-cell">
                                <span class="role-badge">{{ usuario.role && usuario.role.role ? usuario.role.role : 'Sin rol' }}</span>
                            </div>
                            <div class="table-cell">
                                <span :class="['status-badge', usuario.estado_id === 1 ? 'status-active' : 'status-inactive']">
                                    {{ usuario.estado_id === 1 ? 'Activo' : 'Inactivo' }}
                                </span>
                            </div>
                            <div class="table-cell">
                                <div class="action-buttons">
                                    <button class="btn-edit" 
                                            @click="abrirModalUsuario(usuario)" 
                                            :disabled="submitting">
                                        <b-icon icon="pencil-square" class="mr-1"></b-icon>
                                        Editar
                                    </button>
                                    <button class="btn-password" 
                                            @click="abrirModalCambiarPassword(usuario)" 
                                            :disabled="submitting"
                                            title="Cambiar contraseña">
                                        <b-icon icon="key-fill" class="mr-1"></b-icon>
                                        Cambiar Contraseña
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Pagination -->
                <div class="pagination-container" v-if="totalUsuarios > 10">
                    <b-pagination
                        v-model="paginaActual"
                        :total-rows="totalUsuarios"
                        :per-page="10"
                        class="custom-pagination"
                        size="sm"
                    ></b-pagination>
                </div>
            </div>
        </div>

        <!-- Modal para crear/editar usuario -->
        <b-modal
            ref="modalUsuario"
            :title="usuarioEdit ? 'Editar usuario' : 'Crear usuario'"
            hide-footer
            size="md"
            @hide="onModalHide"
            modal-class="custom-modal"
        >
            <div class="modal-form">
                <form @submit.prevent="guardarUsuario" class="user-form">
                    <div class="form-group">
                        <label class="form-label">Nombre</label>
                        <input
                            type="text"
                            class="form-input"
                            v-model="formUsuario.name"
                            required
                            :disabled="submitting"
                            placeholder="Ingresa el nombre completo"
                        >
                    </div>

                    <div class="form-group">
                        <label class="form-label">Correo electronico</label>
                        <input
                            type="email"
                            class="form-input"
                            v-model="formUsuario.email"
                            required
                            :disabled="submitting"
                            placeholder="ejemplo@correo.com"
                        >
                    </div>

                    <div class="form-row">
                        <div class="form-group">
                            <label class="form-label">Rol</label>
                            <select
                                class="form-select"
                                v-model="formUsuario.role_id"
                                required
                                :disabled="submitting"
                            >
                                <option value="">Selecciona un rol</option>
                                <option v-for="rol in rolesOptions" 
                                        :key="rol.value" 
                                        :value="rol.value">
                                    {{ rol.text }}
                                </option>
                            </select>
                        </div>

                        <div class="form-group">
                            <label class="form-label">Estado</label>
                            <select
                                class="form-select"
                                v-model="formUsuario.estado_id"
                                required
                                :disabled="submitting"
                            >
                                <option v-for="estado in estadoOptions" 
                                        :key="estado.value" 
                                        :value="estado.value">
                                    {{ estado.text }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <div v-if="!usuarioEdit" class="form-group">
                        <label class="form-label">Contrasena</label>
                        <input
                            type="password"
                            class="form-input"
                            v-model="formUsuario.password"
                            required
                            :disabled="submitting"
                            placeholder="Contrasena segura"
                        >
                    </div>

                    <div class="form-actions">
                        <button type="button" 
                                class="btn-cancel" 
                                @click="cerrarModal" 
                                :disabled="submitting">
                            Cancelar
                        </button>
                        <button type="submit" 
                                class="btn-save" 
                                :disabled="submitting">
                            <b-spinner small v-if="submitting" class="mr-2"></b-spinner>
                            {{ submitting ? (usuarioEdit ? 'Actualizando...' : 'Creando...') : (usuarioEdit ? 'Actualizar' : 'Crear Usuario') }}
                        </button>
                    </div>
                </form>
            </div>
        </b-modal>

        <!-- Modal para cambiar contraseña -->
        <b-modal
            ref="modalCambiarPassword"
            title="Cambiar Contraseña"
            hide-footer
            size="md"
            @hidden="resetFormPassword"
        >
            <div class="modal-body-custom">
                <div class="user-info-section">
                    <b-icon icon="person-circle" font-scale="2" class="user-icon"></b-icon>
                    <div class="user-details">
                        <h5>{{ usuarioPassword ? usuarioPassword.name : '' }}</h5>
                        <p class="text-muted">{{ usuarioPassword ? usuarioPassword.email : '' }}</p>
                    </div>
                </div>

                <form @submit.prevent="cambiarPassword" class="password-form">
                    <div class="form-group">
                        <label for="new_password" class="form-label">
                            <b-icon icon="lock-fill" class="mr-1"></b-icon>
                            Nueva Contraseña
                        </label>
                        <b-form-input
                            id="new_password"
                            v-model="formPassword.new_password"
                            type="password"
                            placeholder="Ingrese la nueva contraseña"
                            :state="formPassword.new_password.length >= 8 || formPassword.new_password.length === 0 ? null : false"
                            required
                            :disabled="submittingPassword"
                        ></b-form-input>
                        <small class="form-text text-muted">Mínimo 8 caracteres</small>
                    </div>

                    <div class="form-group">
                        <label for="new_password_confirmation" class="form-label">
                            <b-icon icon="lock-fill" class="mr-1"></b-icon>
                            Confirmar Nueva Contraseña
                        </label>
                        <b-form-input
                            id="new_password_confirmation"
                            v-model="formPassword.new_password_confirmation"
                            type="password"
                            placeholder="Confirme la nueva contraseña"
                            :state="formPassword.new_password === formPassword.new_password_confirmation || formPassword.new_password_confirmation === '' ? null : false"
                            required
                            :disabled="submittingPassword"
                        ></b-form-input>
                        <small v-if="formPassword.new_password !== formPassword.new_password_confirmation && formPassword.new_password_confirmation !== ''" 
                               class="form-text text-danger">
                            Las contraseñas no coinciden
                        </small>
                    </div>

                    <div class="modal-footer-custom">
                        <button type="button" 
                                class="btn-cancel" 
                                @click="$refs.modalCambiarPassword.hide()"
                                :disabled="submittingPassword">
                            Cancelar
                        </button>
                        <button type="submit" 
                                class="btn-submit" 
                                :disabled="submittingPassword || formPassword.new_password !== formPassword.new_password_confirmation || formPassword.new_password.length < 8">
                            <b-spinner small v-if="submittingPassword" class="mr-2"></b-spinner>
                            {{ submittingPassword ? 'Cambiando...' : 'Cambiar Contraseña' }}
                        </button>
                    </div>
                </form>
            </div>
        </b-modal>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'UsersIndex',
    data() {
        return {
            usuarios: [],
            fields: [
                { key: 'index', label: '#' },
                { key: 'name', label: 'Nombre' },
                { key: 'email', label: 'Correo' },
                { key: 'role', label: 'Rol' },
                { key: 'estado', label: 'Estado' },
                { key: 'acciones', label: 'Acciones' }
            ],
            paginaActual: 1,
            totalUsuarios: 0,
            usuarioEdit: null,
            formUsuario: {
                name: '',
                email: '',
                role_id: '',
                estado_id: 1,
                password: ''
            },
            rolesOptions: [],
            estadoOptions: [
                { value: 1, text: 'Activo' },
                { value: 2, text: 'Inactivo' }
            ],
            loading: false,
            submitting: false,
            usuarioPassword: null,
            formPassword: {
                new_password: '',
                new_password_confirmation: ''
            },
            submittingPassword: false
        }
    },
    watch: {
        paginaActual: 'cargarUsuarios'
    },
    mounted() {
        document.title = 'Gestion de Usuarios';
        this.cargarUsuarios();
        this.cargarRoles();
    },
    methods: {
        async cargarUsuarios() {
            this.loading = true;
            try {
                const response = await axios.get(`/api/usuarios?page=${this.paginaActual}`, {
                    headers: { Authorization: `Bearer ${this.$store.state.user.token}` }
                });
                // adaptar segun la estructura de la respuesta del backend
                this.usuarios = response.data.usuarios && response.data.usuarios.data ? response.data.usuarios.data : response.data.usuarios || [];
                this.totalUsuarios = response.data.total || response.data.totalUsuarios || this.usuarios.length;
            } catch (err) {
                console.error('Error cargando usuarios:', err);
                this.usuarios = [];
                this.totalUsuarios = 0;
                if (window.Swal) {
                    window.Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudieron cargar los usuarios.' });
                }
            } finally {
                this.loading = false;
            }
        },

        async cargarRoles() {
            try {
                const response = await axios.get('/api/roles', {
                    headers: { Authorization: `Bearer ${this.$store.state.user.token}` }
                });
                this.rolesOptions = response.data.roles && response.data.roles.map ? response.data.roles.map(r => ({ value: r.id, text: r.role })) : [];
            } catch (err) {
                console.error('Error cargando roles:', err);
                this.rolesOptions = [];
            }
        },

        abrirModalUsuario(usuario) {
            if (usuario) {
                this.usuarioEdit = usuario;
                this.formUsuario = {
                    name: usuario.name,
                    email: usuario.email,
                    role_id: usuario.role_id,
                    estado_id: usuario.estado_id,
                    password: ''
                };
            } else {
                this.usuarioEdit = null;
                this.formUsuario = {
                    name: '',
                    email: '',
                    role_id: '',
                    estado_id: 1,
                    password: ''
                };
            }
            this.submitting = false;
            this.$refs.modalUsuario.show();
        },

        cerrarModal() {
            // si esta enviando, no permitir cerrar
            if (this.submitting) return;
            this.$refs.modalUsuario.hide();
        },

        onModalHide() {
            // limpiar formulario al cerrar
            if (!this.submitting) {
                this.usuarioEdit = null;
                this.formUsuario = { name: '', email: '', role_id: '', estado_id: 1, password: '' };
            }
        },

        async guardarUsuario() {
            // prevenir doble submit
            if (this.submitting) return;

            this.submitting = true;

            // mostrar alerta de progreso con Swal (si esta disponible)
            if (window.Swal) {
                window.Swal.fire({
                    title: 'Procesando',
                    text: (this.usuarioEdit ? 'Actualizando usuario...' : 'Creando usuario...'),
                    allowOutsideClick: false,
                    didOpen: () => { window.Swal.showLoading(); }
                });
            }

            try {
                if (this.usuarioEdit) {
                    const { name, email, role_id, estado_id } = this.formUsuario;
                    await axios.put(`/api/usuarios/${this.usuarioEdit.id}`, { name, email, role_id, estado_id }, {
                        headers: { Authorization: `Bearer ${this.$store.state.user.token}` }
                    });
                } else {
                    await axios.post('/api/usuarios', this.formUsuario, {
                        headers: { Authorization: `Bearer ${this.$store.state.user.token}` }
                    });
                }

                // cerrar modal y recargar tabla
                this.$refs.modalUsuario.hide();
                await this.cargarUsuarios();

                if (window.Swal) {
                    window.Swal.fire({ icon: 'success', title: 'Listo', text: (this.usuarioEdit ? 'Usuario actualizado' : 'Usuario creado'), timer: 1400, showConfirmButton: false });
                }
            } catch (err) {
                console.error('Error guardando usuario:', err);
                if (window.Swal) {
                    window.Swal.fire({ icon: 'error', title: 'Error', text: 'Ocurrio un error al guardar el usuario.' });
                }
            } finally {
                this.submitting = false;
                if (window.Swal) window.Swal.close();
            }
        },

        abrirModalCambiarPassword(usuario) {
            // console.log('Abrir modal para cambiar contraseña de:', usuario);
            this.usuarioPassword = usuario;
            this.formPassword = {
                new_password: '',
                new_password_confirmation: ''
            };
            this.$refs.modalCambiarPassword.show();
        },

        resetFormPassword() {
            if (!this.submittingPassword) {
                this.usuarioPassword = null;
                this.formPassword = {
                    new_password: '',
                    new_password_confirmation: ''
                };
            }
        },

        async cambiarPassword() {
            // prevenir doble submit
            if (this.submittingPassword) return;

            // validar que las contraseñas coincidan
            if (this.formPassword.new_password !== this.formPassword.new_password_confirmation) {
                if (window.Swal) {
                    window.Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'Las contraseñas no coinciden'
                    });
                }
                return;
            }

            // validar longitud mínima
            if (this.formPassword.new_password.length < 8) {
                if (window.Swal) {
                    window.Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'La contraseña debe tener al menos 8 caracteres'
                    });
                }
                return;
            }

            this.submittingPassword = true;

            // mostrar alerta de progreso
            if (window.Swal) {
                window.Swal.fire({
                    title: 'Procesando',
                    text: 'Cambiando contraseña...',
                    allowOutsideClick: false,
                    didOpen: () => { window.Swal.showLoading(); }
                });
            }

            const datos = {
                new_password: this.formPassword.new_password,
                new_password_confirmation: this.formPassword.new_password_confirmation,
                email: this.usuarioPassword.email
            }

            try {
                const res = await axios.post(`/api/cambiar-clave`, datos, {
                    headers: { Authorization: `Bearer ${this.$store.state.user.token}` }
                });

                // cerrar modal
                this.$refs.modalCambiarPassword.hide();

                // mostrar mensaje de éxito
                if (res.data.status === true) {
                    window.Swal.fire({
                        icon: 'success',
                        title: 'Éxito',
                        text: 'Contraseña cambiada exitosamente',
                        timer: 2000,
                        showConfirmButton: false
                    });
                }
            } catch (err) {
                console.error('Error cambiando contraseña:', err);
                
                let mensajeError = 'Error al cambiar la contraseña';
                if (err.response && err.response.data && err.response.data.message) {
                    mensajeError = err.response.data.message;
                }

                if (window.Swal) {
                    window.Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: mensajeError
                    });
                }
            } finally {
                this.submittingPassword = false;
            }
        }
    }
}
</script>

<style scoped>
/* Container principal */
.users-container {
    padding: 2rem;
    min-height: 100vh;
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

/* Header Section */
.page-header {
    background: white;
    border-radius: 20px;
    padding: 2rem;
    margin-bottom: 2rem;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 1.5rem;
}

.header-icon {
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, #1a2732 0%, #2a3f4f 100%);
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    box-shadow: 0 8px 20px rgba(26, 39, 50, 0.3);
}

.page-title {
    font-size: 2.5rem;
    font-weight: 700;
    color: #1a2732;
    margin: 0;
    text-shadow: 2px 2px 4px rgba(26, 39, 50, 0.3);
}

.page-subtitle {
    color: #718096;
    font-size: 1.1rem;
    margin: 0.5rem 0 0 0;
    font-weight: 400;
}

.btn-create {
    background: linear-gradient(135deg, #1a2732 0%, #3a4f5f 100%);
    border: none;
    color: white;
    padding: 0.75rem 2rem;
    border-radius: 15px;
    font-weight: 600;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 6px 20px rgba(26, 39, 50, 0.3);
    display: flex;
    align-items: center;
}

.btn-create:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(26, 39, 50, 0.4);
}

/* Content Card */
.content-card {
    background: white;
    border-radius: 20px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.2);
}

/* Loading */
.loading-container {
    padding: 4rem;
    text-align: center;
}

.loading-content {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
}

.loading-spinner {
    width: 3rem;
    height: 3rem;
}

.loading-text {
    color: #718096;
    font-size: 1.1rem;
    margin: 0;
}

/* Custom Table */
.table-container {
    padding: 2rem;
}

.custom-table {
    width: 100%;
    border-radius: 15px;
    overflow: hidden;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
    display: block;
}

.table-header {
    background: linear-gradient(135deg, #1a2732 0%, #2a3f4f 100%);
}

.header-row {
    color: white;
    font-weight: 600;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.table-row {
    display: grid;
    grid-template-columns: 50px minmax(150px, 1fr) minmax(180px, 1.2fr) minmax(100px, 0.8fr) 100px minmax(280px, 1.5fr);
    gap: 0.75rem;
    align-items: center;
    width: 100%;
}

.data-row {
    background: white;
    border-bottom: 1px solid #e2e8f0;
    transition: all 0.3s ease;
}

.data-row:hover {
    background: #f7fafc;
    transform: translateX(5px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.table-cell {
    padding: 0.75rem 0.5rem;
    display: flex;
    align-items: center;
}

.user-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.user-avatar {
    color: #1a2732;
}

.user-name {
    font-weight: 600;
    color: #2d3748;
    word-break: break-word;
    overflow-wrap: break-word;
}

.table-cell:nth-child(3) {
    word-break: break-word;
    overflow-wrap: break-word;
}

.role-badge {
    background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e0 100%);
    color: #1a2732;
    padding: 0.4rem 0.8rem;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 600;
    border: 1px solid #a0aec0;
}

.status-badge {
    padding: 0.4rem 0.8rem;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.status-active {
    background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
    color: #155724;
    border: 1px solid #c3e6cb;
}

.status-inactive {
    background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
    color: #721c24;
    border: 1px solid #f5c6cb;
}

/* Action Buttons Container */
.action-buttons {
    display: flex;
    gap: 0.5rem;
    flex-wrap: nowrap;
    align-items: center;
}

.btn-edit {
    background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
    border: 1px solid #cbd5e0;
    color: #1a2732;
    padding: 0.4rem 0.8rem;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.8rem;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    white-space: nowrap;
}

.btn-edit:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(26, 39, 50, 0.15);
    background: linear-gradient(135deg, #edf2f7 0%, #e2e8f0 100%);
}

.btn-edit:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
}

.btn-password {
    background: linear-gradient(135deg, #ffd89b 0%, #ffa751 100%);
    border: 1px solid #ff9933;
    color: #1a2732;
    padding: 0.4rem 0.8rem;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.8rem;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    white-space: nowrap;
}

.btn-password:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(255, 153, 51, 0.3);
    background: linear-gradient(135deg, #ffa751 0%, #ff8c1a 100%);
}

.btn-password:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
}

/* Pagination */
.pagination-container {
    display: flex;
    justify-content: center;
    margin-top: 2rem;
}

.custom-pagination {
    border-radius: 15px;
    overflow: hidden;
}

/* Modal Styles */
.modal-form {
    padding: 1rem;
}

.user-form {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
}

.form-label {
    color: #2d3748;
    font-weight: 600;
    font-size: 0.9rem;
    margin-bottom: 0.25rem;
}

.form-input,
.form-select {
    padding: 0.75rem 1rem;
    border: 2px solid #e2e8f0;
    border-radius: 10px;
    font-size: 1rem;
    transition: all 0.3s ease;
    background: white;
}

.form-input:focus,
.form-select:focus {
    outline: none;
    border-color: #1a2732;
    box-shadow: 0 0 0 3px rgba(26, 39, 50, 0.1);
}

.form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 1rem;
}

.btn-cancel {
    padding: 0.75rem 1.5rem;
    border: 2px solid #cbd5e0;
    background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
    color: #4a5568;
    border-radius: 10px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
}

.btn-cancel:hover {
    background: linear-gradient(135deg, #edf2f7 0%, #e2e8f0 100%);
    border-color: #a0aec0;
}

.btn-save {
    padding: 0.75rem 1.5rem;
    border: none;
    background: linear-gradient(135deg, #1a2732 0%, #2a3f4f 100%);
    color: white;
    border-radius: 10px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.btn-save:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(26, 39, 50, 0.3);
}

.btn-save:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
}

/* Responsive */
@media (max-width: 768px) {
    .users-container {
        padding: 1rem;
    }
    
    .header-content {
        flex-direction: column;
        gap: 1rem;
        text-align: center;
    }
    
    .page-title {
        font-size: 2rem;
    }
    
    .table-row {
        display: block;
        padding: 1rem;
        border-radius: 10px;
        margin-bottom: 1rem;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    }
    
    .table-header {
        display: none;
    }
    
    .table-cell {
        display: block;
        padding: 0.5rem 0;
        border-bottom: 1px solid #eee;
    }
    
    .table-cell:last-child {
        border-bottom: none;
    }
    
    .form-row {
        display: block;
    }
    
    .form-row .form-group {
        margin-bottom: 1rem;
    }
}

/* Modal Cambiar Contraseña */
.user-info-section {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.5rem;
    background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
    border-radius: 15px;
    margin-bottom: 1.5rem;
    border: 1px solid #e2e8f0;
}

.user-icon {
    color: #4a5568;
}

.user-details h5 {
    margin: 0;
    color: #1a2732;
    font-weight: 700;
    font-size: 1.1rem;
}

.user-details p {
    margin: 0.25rem 0 0 0;
    font-size: 0.9rem;
}

.password-form {
    padding: 0 1rem;
}

.password-form .form-group {
    margin-bottom: 1.5rem;
}

.password-form .form-label {
    font-weight: 600;
    color: #1a2732;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    font-size: 0.95rem;
}

.password-form .form-control {
    border-radius: 10px;
    border: 2px solid #e2e8f0;
    padding: 0.75rem 1rem;
    font-size: 0.95rem;
    transition: all 0.3s ease;
}

.password-form .form-control:focus {
    border-color: #ffa751;
    box-shadow: 0 0 0 0.2rem rgba(255, 167, 81, 0.25);
}

.password-form .form-control.is-invalid {
    border-color: #e53e3e;
}

.password-form .form-control.is-valid {
    border-color: #38a169;
}

.modal-footer-custom {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 2px solid #e2e8f0;
}

.btn-submit {
    padding: 0.75rem 1.5rem;
    border: none;
    background: linear-gradient(135deg, #ffd89b 0%, #ffa751 100%);
    color: #1a2732;
    border-radius: 10px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 1rem;
}

.btn-submit:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 167, 81, 0.3);
    background: linear-gradient(135deg, #ffa751 0%, #ff8c1a 100%);
}

.btn-submit:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
}
</style>